import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { ArrowRight, Loader2 } from "lucide-react";
import {
  INCOME_RANGES,
  EMPLOYMENT_STRUCTURES,
  CONTACT_METHODS,
  LEAD_SOURCES
} from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  profession: "",
  employment_structure: "",
  income_range: "",
  primary_concern: "",
  preferred_contact: "",
  lead_source: "",
  message: ""
};

const inputCls =
  "bg-transparent border-0 border-b border-white/20 rounded-none px-0 h-11 text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-gold";

export default function ConsultationForm() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    const required = [
      "name",
      "email",
      "phone",
      "profession",
      "employment_structure",
      "income_range",
      "primary_concern",
      "preferred_contact"
    ];
    for (const k of required) {
      if (!form[k]) {
        toast.error("Please complete all required fields.");
        return;
      }
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Thank you. We'll reach out to schedule your introductory consultation.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const selectTriggerCls =
    "bg-transparent border-0 border-b border-white/20 rounded-none px-0 h-11 text-white data-[placeholder]:text-white/30 focus:ring-0 focus:border-gold";

  return (
    <form data-testid="consultation-form" onSubmit={submit} className="grid gap-8 sm:grid-cols-2">
      <div className="flex flex-col gap-2">
        <Label className="text-xs uppercase tracking-[0.15em] text-white/50">Full Name</Label>
        <Input data-testid="form-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" className={inputCls} />
      </div>
      <div className="flex flex-col gap-2">
        <Label className="text-xs uppercase tracking-[0.15em] text-white/50">Email</Label>
        <Input data-testid="form-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
      </div>
      <div className="flex flex-col gap-2">
        <Label className="text-xs uppercase tracking-[0.15em] text-white/50">Phone</Label>
        <Input data-testid="form-phone" value={form.phone} onChange={set("phone")} placeholder="(000) 000-0000" className={inputCls} />
      </div>
      <div className="flex flex-col gap-2">
        <Label className="text-xs uppercase tracking-[0.15em] text-white/50">Profession</Label>
        <Input data-testid="form-profession" value={form.profession} onChange={set("profession")} placeholder="CRNA, Physician, APP…" className={inputCls} />
      </div>

      <div className="flex flex-col gap-2">
        <Label className="text-xs uppercase tracking-[0.15em] text-white/50">Employment Structure</Label>
        <Select value={form.employment_structure} onValueChange={set("employment_structure")}>
          <SelectTrigger data-testid="form-employment" className={selectTriggerCls}>
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent className="bg-navy-soft border-white/10 text-white">
            {EMPLOYMENT_STRUCTURES.map((o) => (
              <SelectItem key={o} value={o} className="focus:bg-white/10 focus:text-white">{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-2">
        <Label className="text-xs uppercase tracking-[0.15em] text-white/50">Estimated Annual Income</Label>
        <Select value={form.income_range} onValueChange={set("income_range")}>
          <SelectTrigger data-testid="form-income" className={selectTriggerCls}>
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent className="bg-navy-soft border-white/10 text-white">
            {INCOME_RANGES.map((o) => (
              <SelectItem key={o} value={o} className="focus:bg-white/10 focus:text-white">{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-2">
        <Label className="text-xs uppercase tracking-[0.15em] text-white/50">Preferred Contact Method</Label>
        <Select value={form.preferred_contact} onValueChange={set("preferred_contact")}>
          <SelectTrigger data-testid="form-contact-method" className={selectTriggerCls}>
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent className="bg-navy-soft border-white/10 text-white">
            {CONTACT_METHODS.map((o) => (
              <SelectItem key={o} value={o} className="focus:bg-white/10 focus:text-white">{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-2">
        <Label className="text-xs uppercase tracking-[0.15em] text-white/50">How did you hear about us? (optional)</Label>
        <Select value={form.lead_source} onValueChange={set("lead_source")}>
          <SelectTrigger data-testid="form-lead-source" className={selectTriggerCls}>
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent className="bg-navy-soft border-white/10 text-white">
            {LEAD_SOURCES.map((o) => (
              <SelectItem key={o} value={o} className="focus:bg-white/10 focus:text-white">{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-2">
        <Label className="text-xs uppercase tracking-[0.15em] text-white/50">Primary Financial Concern</Label>
        <Input data-testid="form-concern" value={form.primary_concern} onChange={set("primary_concern")} placeholder="Taxes, cash flow, retirement…" className={inputCls} />
      </div>

      <div className="sm:col-span-2 flex flex-col gap-2">
        <Label className="text-xs uppercase tracking-[0.15em] text-white/50">Anything else? (optional)</Label>
        <Textarea data-testid="form-message" value={form.message} onChange={set("message")} placeholder="Tell us a little about where you're at." className="bg-transparent border border-white/20 rounded-sm text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-gold min-h-[110px]" />
      </div>

      <div className="sm:col-span-2">
        <button
          data-testid="form-submit"
          type="submit"
          disabled={loading}
          className="group inline-flex items-center gap-3 rounded-sm bg-gold px-8 py-4 text-sm font-medium text-navy-deep hover:bg-gold-hover transition-colors duration-300 disabled:opacity-60"
        >
          {loading ? <Loader2 className="animate-spin" size={18} /> : null}
          Request My Introductory Consultation
          <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </div>
    </form>
  );
}
